import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Users, Search, ArrowRight, ArrowLeft, Check, Sparkles } from 'lucide-react';
import { useClient } from '../../context/ClientContext';
import { useTheme } from '../../context/ThemeContext';
import ClientWorkspaceHeader from './ClientWorkspaceHeader';
import ClientAvatar from '../admin/ClientAvatar';
import { TOPBAR_CLIENT_PICKED_EVENT } from '../tour/tourElementUtils';

export default function ClientWorkspaceLayout({ children, fillViewport = false }) {
  const { selectedClient, setSelectedClient, clients, isLoadingClients } = useClient();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'english');
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const t = (en, he) => (language === 'hebrew' ? he : en);

  const pulseRank = (p) => ({ healthy: 0, new: 1, '24h': 2, '72h': 3, never: 4, onboarding: 5 }[p] ?? 6);

  const filteredClients = useMemo(() => {
    let list = Array.isArray(clients) ? clients : [];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((c) => {
        const name = (c.full_name || c.name || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const code = (c.user_code || '').toLowerCase();
        const phone = (c.phone_number || '').toLowerCase();
        return name.includes(q) || email.includes(q) || code.includes(q) || phone.includes(q);
      });
    }
    return [...list].sort((a, b) => pulseRank(a.engagementPulse) - pulseRank(b.engagementPulse));
  }, [clients, searchQuery]);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    window.dispatchEvent(new CustomEvent(TOPBAR_CLIENT_PICKED_EVENT, { detail: client }));
  };

  // If a client is selected, render the workspace header + content
  if (selectedClient) {
    return (
      <div
        className={
          fillViewport
            ? 'h-[calc(100dvh-3.5rem)] sm:h-[calc(100dvh-4rem)] xl:h-[calc(100dvh-4.5rem)] flex flex-col overflow-hidden'
            : 'min-h-full flex flex-col flex-1'
        }
        dir={direction}
      >
        <div className="shrink-0">
          <ClientWorkspaceHeader />
        </div>
        <div className={fillViewport ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : 'flex-1 flex flex-col min-w-0'}>
          {children}
        </div>
      </div>
    );
  }

  // Calm Empty State when no client is currently selected
  return (
    <div
      className={`min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 sm:p-6 lg:p-8 transition-colors ${
        isDarkMode ? 'bg-canvas-dark text-white' : 'bg-canvas text-ink'
      }`}
      dir={direction}
    >
      <div
        className={`w-full max-w-xl rounded-lg border  p-6 sm:p-8 text-center transition-all ${
          isDarkMode ? 'bg-surface-dark border-line-dark' : 'bg-surface border-line'
        }`}
      >
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg mx-auto mb-4 flex items-center justify-center bg-forest-faint dark:bg-forest/20 text-forest dark:text-forest-ondark border border-forest/20">
          <Users size={28} />
        </div>

        <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-2">
          {t('Select a client to view their workspace', 'בחרו לקוח כדי לפתוח את מרחב העבודה')}
        </h2>
        <p className="text-xs sm:text-sm text-ink-muted dark:text-ink-muted-dark max-w-md mx-auto mb-6">
          {t(
            'Choose from your active roster or search below to review client overview, messages, nutrition plans, workouts and analytics.',
            'בחרו לקוח מהרשימה או חפשו למטה כדי לצפות בסקירה, בהודעות, בתוכניות התזונה, באימונים ובאנליטיקה.'
          )}
        </p>

        {/* Inline Search Input */}
        <div className="relative mb-4 text-start">
          <Search size={18} className="absolute top-1/2 -translate-y-1/2 start-3.5 text-ink-muted dark:text-ink-muted-dark pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Search client by name, email, phone or code...', 'חיפוש לקוח לפי שם, אימייל, טלפון או קוד...')}
            className={`w-full py-2.5 ps-10 pe-4 text-sm rounded-lg border outline-none transition-colors ${
              isDarkMode
                ? 'bg-canvas-dark border-line-dark focus:border-forest text-white'
                : 'bg-canvas border-line focus:border-forest text-ink'
            }`}
          />
        </div>

        {/* Inline Client Roster (Ordered by engagement) */}
        <div className="max-h-64 overflow-y-auto rounded-lg border border-rule divide-y divide-rule text-start p-1 mb-6">
          {isLoadingClients ? (
            <div className="py-8 text-center text-xs text-ink-muted">
              {t('Loading clients...', 'טוען לקוחות...')}
            </div>
          ) : filteredClients.length > 0 ? (
            filteredClients.map((client) => {
              const name = client.full_name || client.name || client.user_code || t('Unknown Client', 'לקוח');
              return (
                <button
                  key={client.id || client.user_code}
                  type="button"
                  onClick={() => handleSelectClient(client)}
                  className={`w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-between gap-3 text-start ${
                    isDarkMode ? 'hover:bg-canvas-dark text-white' : 'hover:bg-canvas text-ink'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <ClientAvatar client={client} size="sm" />
                    <div className="min-w-0">
                      <div className="font-semibold truncate text-sm">{name}</div>
                      <div className="text-xs text-ink-muted dark:text-ink-muted-dark truncate">
                        {client.email || client.phone_number || client.user_code}
                      </div>
                    </div>
                  </div>
                  {client.engagementPulse === 'healthy' && (
                    <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-forest-faint text-forest dark:bg-forest/20 dark:text-forest-ondark">
                      {t('Active', 'פעיל/ה')}
                    </span>
                  )}
                </button>
              );
            })
          ) : (
            <div className="py-8 text-center text-xs text-ink-muted">
              {t('No clients found', 'לא נמצאו לקוחות')}
            </div>
          )}
        </div>

        {/* Link back to Overview */}
        <Link
          to="/dietitian-dashboard"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-forest dark:text-forest-ondark hover:underline"
        >
          <span>{t('Go to Practice Overview', 'חזרה לסקירה כללית')}</span>
          {direction === 'rtl' ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
        </Link>
      </div>
    </div>
  );
}
